import type { ChannelId } from './channel.js';
// ENG-7025 / ADR-0032: the agent's kind ('standard' | 'system_support') is the
// single source of truth in the support-agent module. Type-only import keeps
// this a compile-time edge (no runtime cycle with the generation layer).
import type { SupportAgentKind } from '../generation/support-agent.js';

export type Environment = 'dev' | 'stage' | 'prod';
export type RiskTier = 'Low' | 'Medium' | 'High';
export type AgentStatus = 'draft' | 'active' | 'paused' | 'revoked';
export type ReportsToType = 'agent' | 'person';

/**
 * Framework runtimes an agent can run under. ENG-6932 (hard removal, following
 * the ENG-6919 soft deprecation): OpenClaw, NemoClaw and the Anthropic Managed
 * Agents adapters have been removed; Claude Code is the only supported
 * framework. The fleet was fully migrated to claude-code before this narrowing.
 */
export type FrameworkId = 'claude-code';

/** The single supported framework for newly provisioned agents and hosts. */
export const DEFAULT_FRAMEWORK: FrameworkId = 'claude-code';

/**
 * Source of truth for framework deprecation. `satisfies Record<FrameworkId, …>`
 * keeps this map in lockstep with the FrameworkId union. After ENG-6932 only
 * claude-code remains, so nothing is deprecated; the map and the predicate are
 * retained so legacy framework strings still read on historical rows resolve to
 * "not deprecated" without throwing.
 */
export const FRAMEWORK_DEPRECATION = {
  'claude-code': false,
} as const satisfies Record<FrameworkId, boolean>;

/** True when `id` is a known, deprecated framework. Unknown ids are treated as not deprecated. */
export function isDeprecatedFramework(id: string): boolean {
  return (FRAMEWORK_DEPRECATION as Record<string, boolean>)[id] === true;
}

export interface AgentStandup {
  yesterday: string;
  today: string;
  blockers: string;
  updated_at: string;
}

export type AuthProfileType = 'api_key' | 'oauth';

export interface AgentAuthProfile {
  id: string;
  agent_id: string;
  team_id: string;
  provider: string;
  profile_name: string;
  auth_type: AuthProfileType;
  api_key?: string;
  metadata: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Agent {
  agent_id: string;
  team_id: string;
  code_name: string;
  display_name: string;
  description?: string;
  role?: string | null;
  created_by: string;
  owner: string;
  environment: Environment;
  risk_tier: RiskTier;
  status: AgentStatus;
  /**
   * ENG-7025 / ADR-0032: agent kind. Defaults to 'standard'; 'system_support'
   * marks the auto-provisioned per-org Augmented Support agent. Optional here so
   * historical rows read before the column existed still type-check.
   */
  agent_kind?: SupportAgentKind;
  /**
   * ENG-5561: why the agent is currently paused ('manual', 'hourly_cost_exceeded').
   * Set when status flips to paused, cleared on resume. NULL when active or
   * paused by a pre-column path — status is the source of truth for paused-ness.
   */
  paused_reason?: string | null;
  framework: FrameworkId;
  /** Anthropic Managed Agents API agent ID — set when framework='managed-agents' and synced */
  anthropic_agent_id?: string | null;
  /** Anthropic Managed Agents API environment ID — set at sync time */
  anthropic_environment_id?: string | null;
  session_mode: 'oneshot' | 'persistent';
  charter_version_id?: string;
  tools_version_id?: string;
  budget_tokens_per_day?: number;
  budget_dollars_per_month?: number;
  /**
   * ENG-5559: per-agent USD/hr cost ceiling for the hourly cost guardrail
   * (ENG-5556). null/undefined = inherit team → global default. Most-specific
   * scope wins; resolved by resolveHourlyCostLimitUsd().
   */
  hourly_cost_limit_usd?: number | null;
  avatar_url?: string;
  /**
   * ENG-5733: optional 128×128 thumbnail URL, generated alongside the
   * 512×512 avatar_url. UI components render this in lists / cards for
   * fast loads; downloads (Slack profile picture export, modal preview)
   * still consume avatar_url.
   */
  avatar_thumb_url?: string;
  channels: ChannelId[];
  reports_to?: string | null;
  reports_to_type?: ReportsToType;
  last_heartbeat_at?: string | null;
  personality_seed?: string | null;
  primary_model?: string | null;
  secondary_model?: string | null;
  tertiary_model?: string | null;
  /**
   * ENG-6695: per-agent IANA timezone override for restart-deferral /
   * maintenance-window scheduling. `null` = inherit (team → org → UTC); see
   * `resolveAgentTimezone`.
   */
  timezone?: string | null;
  standup?: AgentStandup | null;
  current_tasks?: string | null;
  diagnostics?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}
